import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

// @ts-expect-error - imported from .js/.jsx file

import {CompanyIcon} from 'shells/icon/svg/company-icon';
import {ContactIcon} from 'shells/icon/svg/contact-icon';
import {LoadingPage} from 'shells/loading-page/loading-page';

import type {WidgetFragment as Widget} from 'nutshell-graphql-types';

import {CardWidgetWrapper} from '../../../core/card/card-widget-wrapper';
import {CollapsibleCardWidget} from '../../../core/card/collapsible-card-widget';
import {getWidgetEmptyStateProps} from '../../../core/card/helpers';
import type {Option} from '../../../core/select/graphql-select-creatable';
import {useGetSessionUser} from '../../../core/use-get-session-user';
import {EmailModalWrapper} from '../../../core-entity/components/email-modal-wrapper';
import {useCreateAccountForCoreEntity} from '../../../core-entity/graphql/hooks/use-create-account-for-core-entity';
import {useCreateContactForCoreEntity} from '../../../core-entity/graphql/hooks/use-create-contact-for-core-entity';
import {ContactRow} from '../../../core-entity/rows/peep-rows/contact-row';

import {refetchForEvent} from '../../refetch-helpers';

import {ContactRowCreateForm, CompanyRowCreateForm} from './components';
import {PeepsRow} from './components/peeps-row';
import {
    useGetContactsForLead,
    useGetAccountsForLead,
    useUpdateContactForLead,
    useUpdateAccountForLead,
    useRemoveContactForLead,
    useRemoveAccountForLead,
} from './graphql/hooks';
import {getAccountsAndContactsCount, refetchAccountsAndContactsQueries} from './helpers';

type Props = {
    leadId: string;
    widget?: Widget;
};

export function AccountsAndContactsWidgetWrapper(props: Props) {
    const [emailModalSeededEmailAddress, setEmailModalSeededEmailAddress] = React.useState<
        string | null | undefined
    >(null);
    const [emailModalSeededWithAllEmailAddresses, setEmailModalSeededWithAllEmailAddresses] =
        React.useState<boolean>(false);
    const [isCreatingContact, setIsCreatingContact] = React.useState<boolean>(false);
    const [contactParentAccount, setContactParentAccount] = React.useState<
        Option | null | undefined
    >(null);
    const [isCreatingAccount, setIsCreatingAccount] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null | undefined>(null);

    const {
        accounts,
        isLoading: isLoadingAccounts,
        refetch: refetchAccounts,
    } = useGetAccountsForLead(props.leadId);
    const {
        contacts,
        isLoading: isLoadingContacts,
        refetch: refetchContacts,
    } = useGetContactsForLead(props.leadId);
    const accountsAndContactsCount = getAccountsAndContactsCount(accounts, contacts);
    const {user, isLoading: isLoadingUser} = useGetSessionUser();
    const phoneCallerType = user && user.phoneCallerType;

    const refetchQueries = refetchAccountsAndContactsQueries({leadId: props.leadId});

    const {createContactForCoreEntity, isLoading: isLoadingCreateContact} =
        useCreateContactForCoreEntity({
            refetchQueries: refetchQueries,
        });
    const {createAccountForCoreEntity, isLoading: isLoadingCreateAccount} =
        useCreateAccountForCoreEntity({
            refetchQueries: refetchQueries,
        });
    const {updateContact, isLoading: isLoadingUpdateContact} = useUpdateContactForLead(
        props.leadId
    );
    const {updateAccount, isLoading: isLoadingUpdateAccount} = useUpdateAccountForLead(
        props.leadId
    );
    const {removeContact} = useRemoveContactForLead();
    const {removeAccount} = useRemoveAccountForLead();

    const isLoading =
        (!contacts || !accounts) && (isLoadingAccounts || isLoadingContacts || isLoadingUser);

    const isRefetching =
        isLoadingCreateContact ||
        isLoadingUpdateContact ||
        isLoadingCreateAccount ||
        isLoadingUpdateAccount;

    const userCanUseSms = user && user.permissions && user.permissions.canUseSms;
    const sendFromNutshellEnabled = user && user.sendFromNutshellEnabled;
    const hasConnectedPhoneIntegration = Boolean(
        user &&
            (user.phoneIntegrations.some(
                (integration) => integration && integration.isEnabledForUser
            ) ||
                (user.clickToCallIntegration && user.clickToCallIntegration.isEnabled))
    );

    const alreadyAddedContacts = React.useMemo(() => {
        const contactNodes =
            contacts && contacts.nonCompanyMappedContacts
                ? contacts.nonCompanyMappedContacts.map((contact) => contact.node)
                : [];

        // @ts-expect-error
        let accountContactNodes = [];
        if (accounts && accounts.edges) {
            accounts.edges.forEach((account) => {
                if (account.leadAccountContacts) {
                    // @ts-expect-error
                    accountContactNodes = accountContactNodes.concat(account.leadAccountContacts);
                }
            });
        }

        // @ts-expect-error
        return [...contactNodes, ...accountContactNodes];
    }, [contacts, accounts]);

    // Handle refetch when adding a contact via Nutshell PeopleIQ sidebar
    React.useEffect(() => {
        const handleContactsUpdated = () => {
            refetchAccounts();
        };

        $('body').on(Nut.events.global.accountContactsWereUpdated, handleContactsUpdated);

        return () => {
            $('body').off(Nut.events.global.accountContactsWereUpdated, handleContactsUpdated);
        };
    }, [refetchAccounts]);

    const handleCreateContact = () => {
        setIsCreatingContact(true);
    };

    const handleCreateAccount = () => {
        setIsCreatingAccount(true);
    };

    const handleRemoveContact = (contactId: string, contactParentAccountId?: string) => {
        if (contactParentAccountId) {
            setContactParentAccount({id: contactParentAccountId, name: ''});
        } else {
            setContactParentAccount(null);
        }

        removeContact({
            lead: props.leadId,
            contact: contactId,
        })
            .then(() => {
                if (NutClientConfig.hasLeadPageRedesignM3FF) {
                    refetchForEvent('contactsUpdated');
                } else {
                    $(window).trigger(Lead.events.LeadBackboneModelDidUpdate);
                }
            })
            .catch((e) => {
                setError(safelyGetGraphQLErrorMessage(e));
            });
    };

    const handleRemoveAccount = (accountId: string) => {
        setContactParentAccount(null);
        removeAccount({
            lead: props.leadId,
            account: accountId,
        }).catch((e) => {
            setError(safelyGetGraphQLErrorMessage(e));
        });
    };

    const selectMenuOptions = [
        {
            icon: <CompanyIcon size={16} wrapWithDiv={false} />,
            label: 'Add a company',
            value: 'company',
            handleSelect: () => {
                handleCreateAccount();
                setContactParentAccount(null);
            },
        },
        {
            icon: <ContactIcon size={16} wrapWithDiv={false} />,
            label: 'Add a person',
            value: 'person',
            handleSelect: () => {
                handleCreateContact();
                setContactParentAccount(null);
            },
        },
    ];

    const CreateContactForm = (
        <ContactRowCreateForm
            leadId={props.leadId}
            onClose={() => setIsCreatingContact(false)}
            onCreate={createContactForCoreEntity}
            onUpdate={updateContact}
            contactParentAccount={contactParentAccount}
            alreadyAddedAccountIds={
                accounts && accounts.edges ? accounts.edges.map((account) => account.node.id) : []
            }
            errorProps={{
                error: error,
                setError: setError,
            }}
            alreadyAddedContacts={alreadyAddedContacts || []}
        />
    );

    // TODO: Remove this once we move the hoverview popover and duplicate
    // popover to React
    React.useEffect(() => {
        const handleEipUpdate = () => {
            // Not attempting to parse the various events and data that are
            // passed by jQuery as their structure and content varies by event.
            // These events aren't very common, so we can just refetch the data
            // always.
            refetchAccounts();
            refetchContacts();
        };

        $(window).on('EipDidUpdateGlobal', handleEipUpdate);

        return () => {
            $(window).off('EipDidUpdateGlobal', handleEipUpdate);
        };
    }, [refetchAccounts, refetchContacts]);

    const handleOpenEmailModal = (emailAddress: string) => {
        setEmailModalSeededEmailAddress(emailAddress);
    };

    const isEmpty =
        accountsAndContactsCount === 0 && !isCreatingContact && !isCreatingAccount && !isRefetching;
    const addedAccountIds = accounts && accounts.edges.map((account) => account.node.id);

    const content = (
        <>
            {isCreatingContact && !contactParentAccount && CreateContactForm}
            {isRefetching && !contactParentAccount && <LoadingPage padding={8} />}
            {isCreatingAccount && (
                <CompanyRowCreateForm
                    leadId={props.leadId}
                    onClose={() => setIsCreatingAccount(false)}
                    onCreate={createAccountForCoreEntity}
                    onUpdate={updateAccount}
                    errorProps={{
                        error: error,
                        setError: setError,
                    }}
                    alreadyAddedAccountIds={addedAccountIds}
                />
            )}
            {contacts && (
                <>
                    {accounts &&
                        accounts.edges.map((account) => (
                            <PeepsRow
                                key={account.node.id}
                                leadId={props.leadId}
                                account={account}
                                isCreatingContact={isCreatingContact}
                                contactParentAccount={contactParentAccount}
                                isRefetching={isRefetching}
                                phoneCallerType={phoneCallerType}
                                userCanUseSms={userCanUseSms}
                                sendFromNutshellEnabled={sendFromNutshellEnabled}
                                CreateContactForm={CreateContactForm}
                                setIsCreatingContact={setIsCreatingContact}
                                setContactParentAccount={setContactParentAccount}
                                handleRemoveAccount={handleRemoveAccount}
                                handleOpenEmailModal={handleOpenEmailModal}
                                handleRemoveContact={handleRemoveContact}
                                setError={setError}
                                hasConnectedPhoneIntegration={hasConnectedPhoneIntegration}
                            />
                        ))}
                    {contacts.nonCompanyMappedContacts &&
                        contacts.nonCompanyMappedContacts.map((contact) => (
                            <ContactRow
                                contactId={contact.node.id}
                                key={contact.node.id}
                                name={contact.node.name}
                                contactUrl={contact.node.htmlUrlPath}
                                description={contact.node.description}
                                role={contact.node.jobTitle}
                                phone={
                                    contact.node.phones.primary && contact.node.phones.primary.value
                                }
                                phoneCallerType={phoneCallerType}
                                userCanUseSms={userCanUseSms}
                                email={contact.node.primaryEmailAddress}
                                lastContactedTime={contact.node.lastContactedTime}
                                isDuplicate={contact.node.hasDuplicates}
                                avatarUrl={contact.node.avatarUrl}
                                parentEntityName='Leads'
                                onOpenEmailModal={handleOpenEmailModal}
                                removeContact={handleRemoveContact}
                                setError={setError}
                                hasConnectedPhoneIntegration={hasConnectedPhoneIntegration}
                                sendFromNutshellEnabled={sendFromNutshellEnabled}
                            />
                        ))}
                </>
            )}
        </>
    );

    return (
        <>
            {NutClientConfig.hasLeadPageRedesignM3FF ? (
                props.widget && (
                    <CollapsibleCardWidget
                        widget={props.widget}
                        addButtonProps={{
                            variant: 'multiple',
                            isDisabled: isCreatingContact || isCreatingAccount,
                            selectMenuOptions: selectMenuOptions,
                        }}
                        isLoading={!isRefetching && isLoading}
                        errorProps={{
                            error: error,
                            setError: setError,
                        }}
                        emptyStateProps={getWidgetEmptyStateProps(
                            'leadCompaniesAndPeople',
                            isEmpty
                        )}
                        count={accountsAndContactsCount}
                        setIsAdding={(isAdding) => {
                            setIsCreatingContact(isAdding);
                            setIsCreatingAccount(isAdding);
                        }}
                    >
                        {content}
                    </CollapsibleCardWidget>
                )
            ) : (
                <CardWidgetWrapper
                    widgetKey='leadCompaniesAndPeople'
                    addButtonProps={{
                        variant: 'multiple',
                        isDisabled: isCreatingContact || isCreatingAccount,
                        selectMenuOptions: selectMenuOptions,
                    }}
                    count={accountsAndContactsCount}
                    isLoading={!isRefetching && isLoading}
                    isEmpty={isEmpty}
                    errorProps={{
                        error: error,
                        setError: setError,
                    }}
                >
                    {content}
                </CardWidgetWrapper>
            )}

            {(emailModalSeededEmailAddress || emailModalSeededWithAllEmailAddresses) && (
                <EmailModalWrapper
                    entityId={props.leadId}
                    postModalOpen={() => {
                        setEmailModalSeededEmailAddress(null);
                        setEmailModalSeededWithAllEmailAddresses(false);
                    }}
                    seededWithEmailAddress={emailModalSeededEmailAddress}
                    seedWithAllPrimaryRelatedEmailAddresses={emailModalSeededWithAllEmailAddresses}
                />
            )}
        </>
    );
}
